<template>
  <View direction="column" gap="sm">
    <CodeInput
      v-if="mode === 'totp'"
      ref="codeInputRef"
      v-model="model"
      :disabled="disabled"
      @complete="emit('complete', $event)"
    />
    <Input
      v-else
      :model-value="model"
      :disabled="disabled"
      placeholder="XXXX-XXXX"
      autocomplete="one-time-code"
      maxlength="9"
      @update:model-value="model = String($event).toUpperCase()"
    />
    <Text as="div" variant="body-medium">
      <Link color="accent" @click="toggleMode">
        {{ mode === 'totp' ? 'Use a backup code instead' : 'Use an authenticator code instead' }}
      </Link>
    </Text>
  </View>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';

import { CodeInput, Input, Link, Text, View } from '@tallo/design-system';

/**
 * Entry for the two shapes a second factor takes: 6-digit TOTP boxes by default, and a
 * plain field for XXXX-XXXX backup codes, which don't fit digit boxes. Backup codes are
 * stored uppercase, so typed input is normalized before it reaches the server.
 */
const model = defineModel<string>({ default: '' });
const emit = defineEmits<{ complete: [code: string] }>();

defineProps<{ disabled?: boolean }>();

const codeInputRef = ref<InstanceType<typeof CodeInput>>();
const mode = ref<'totp' | 'backup'>('totp');

async function toggleMode() {
  mode.value = mode.value === 'totp' ? 'backup' : 'totp';
  model.value = '';

  if (mode.value === 'totp') {
    await focus();
  }
}

async function focus() {
  await nextTick();
  codeInputRef.value?.focus();
}

defineExpose({ focus });
</script>
